<script setup>
import { CheckCircle2, AlertCircle, Info, X } from '@lucide/vue';
import { useToast } from '../../composables/useToast.js';

const { toasts, dismiss } = useToast();

// Per-type icon + accent class. Success uses the brand accent (--primary),
// error uses --destructive, info uses muted foreground — all variant tokens,
// so toasts re-skin automatically with the active theme variant.
const META = {
    success: { icon: CheckCircle2, accent: 'var(--primary)' },
    error: { icon: AlertCircle, accent: 'var(--destructive)' },
    info: { icon: Info, accent: 'var(--muted-foreground)' },
};
</script>

<template>
    <div
        class="aia-toaster fixed top-4 right-4 flex flex-col gap-2 pointer-events-none"
        style="z-index: var(--aia-z);"
        aria-live="polite"
        aria-atomic="false"
    >
        <TransitionGroup name="aia-toast">
            <div
                v-for="toast in toasts"
                :key="toast.id"
                class="aia-toast pointer-events-auto flex items-start gap-2.5 w-[min(340px,calc(100vw-2rem))] rounded-xl border border-[var(--border)] bg-[var(--card)] px-3.5 py-3 text-sm text-[var(--card-foreground)] shadow-2xl"
                :style="{ borderInlineStartWidth: '3px', borderInlineStartColor: (META[toast.type] || META.info).accent }"
                role="status"
            >
                <component
                    :is="(META[toast.type] || META.info).icon"
                    :size="18"
                    class="mt-0.5 shrink-0"
                    :style="{ color: (META[toast.type] || META.info).accent }"
                    aria-hidden="true"
                />
                <span class="flex-1 leading-snug">{{ toast.message }}</span>
                <button
                    type="button"
                    class="shrink-0 -mr-1 -mt-0.5 rounded p-1 text-[var(--muted-foreground)] hover:text-[var(--foreground)] transition-colors"
                    aria-label="Dismiss"
                    @click="dismiss(toast.id)"
                >
                    <X :size="14" />
                </button>
            </div>
        </TransitionGroup>
    </div>
</template>

<style scoped>
.aia-toast-enter-active,
.aia-toast-leave-active {
    transition: opacity 240ms ease, transform 240ms cubic-bezier(0.2, 0.65, 0.25, 1);
}
.aia-toast-enter-from {
    opacity: 0;
    transform: translateX(16px);
}
.aia-toast-leave-to {
    opacity: 0;
    transform: translateX(16px);
}
/* Smoothly slide remaining toasts when one above is removed. */
.aia-toast-move {
    transition: transform 240ms cubic-bezier(0.2, 0.65, 0.25, 1);
}
@media (prefers-reduced-motion: reduce) {
    .aia-toast-enter-active,
    .aia-toast-leave-active,
    .aia-toast-move {
        transition: none;
    }
}
</style>
